import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchLiveMonitor } from '../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const COLORS = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4','#8b5cf6','#ef4444'];
const avatarColor = (name: string) =>
  COLORS[name.charCodeAt(0) % COLORS.length];

const fmtSecs = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const fmtTimeAgo = (iso: string | null) => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Avatar ───────────────────────────────────────────────────────────────────

const Avatar: React.FC<{ name: string; size?: number; pulse?: boolean }> = ({ name, size = 40, pulse }) => (
  <div style={{ position: 'relative', flexShrink: 0 }}>
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: avatarColor(name), color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.34, fontWeight: 700,
    }}>
      {initials(name)}
    </div>
    {pulse && (
      <span style={{
        position: 'absolute', bottom: 1, right: 1,
        width: 11, height: 11, borderRadius: '50%',
        background: '#22c55e', border: '2px solid #fff',
      }} />
    )}
  </div>
);

// ─── Live call timer ──────────────────────────────────────────────────────────

const LiveTimer: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    const base = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    setSecs(base);
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  return (
    <div style={{ fontSize: 28, fontWeight: 800, color: '#22c55e', letterSpacing: 1, fontVariantNumeric: 'tabular-nums' }}>
      {fmtSecs(secs)}
    </div>
  );
};

// ─── Total talk timer ─────────────────────────────────────────────────────────

const TalkTimer: React.FC<{ initialSecs: number }> = ({ initialSecs }) => {
  const [secs, setSecs] = useState(initialSecs);
  useEffect(() => {
    setSecs(initialSecs);
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [initialSecs]);
  return <>{fmtSecs(secs)}</>;
};

// ─── Active call card ─────────────────────────────────────────────────────────

const ActiveCallCard: React.FC<{ agent: any }> = ({ agent }) => {
  const history = useHistory();
  const isOut = agent.current_call_direction === 'outgoing';

  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: '1.5px solid #e2e8f0',
      padding: 20, minWidth: 280, flex: '1 1 280px', maxWidth: 380,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      {/* Agent row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Avatar name={agent.agent_name} size={42} pulse />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{agent.agent_name}</div>
          <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>● ON CALL</div>
        </div>
        <span style={{
          background: isOut ? '#dcfce7' : '#dbeafe',
          color: isOut ? '#166534' : '#1d4ed8',
          borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
        }}>
          {isOut ? '↑ OUT' : '↓ IN'}
        </span>
      </div>

      {/* Customer */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--navy)' }}>
          {agent.current_call_phone}
        </div>
      </div>

      {/* Timer */}
      {agent.current_call_started_at && (
        <LiveTimer startedAt={agent.current_call_started_at} />
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          disabled
          title="SIM calls cannot be monitored from browser"
          style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', fontSize: 12, cursor: 'not-allowed' }}
        >
          🎧 Listen
        </button>
        <button
          disabled
          title="SIM calls cannot be monitored from browser"
          style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', fontSize: 12, cursor: 'not-allowed' }}
        >
          ✏️ Whisper
        </button>
        {agent.lead_id && (
          <button
            onClick={() => history.push(`/leads/${agent.lead_id}`)}
            style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: 'var(--navy)', fontSize: 12, cursor: 'pointer' }}
          >
            👤 Profile
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Agent chip (idle / offline) ─────────────────────────────────────────────

const AgentChip: React.FC<{ agent: any; dimmed?: boolean }> = ({ agent, dimmed }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    background: dimmed ? '#f8fafc' : '#fff',
    border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '8px 14px', opacity: dimmed ? 0.6 : 1,
  }}>
    <Avatar name={agent.agent_name} size={30} />
    <div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{agent.agent_name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        last call {fmtTimeAgo(agent.last_call_at)}
      </div>
    </div>
  </div>
);

// ─── Stat card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: React.ReactNode; color?: string; live?: boolean }> = ({
  label, value, color, live,
}) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 22px', flex: 1, minWidth: 140 }}>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      {live && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />}
      {label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 800, color: color ?? 'var(--navy)', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const LiveMonitorPage: React.FC = () => {
  const [data, setData]           = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError]         = useState('');
  const timerRef                  = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = () => {
    fetchLiveMonitor()
      .then(d => { setData(d); setLastUpdate(new Date()); setError(''); })
      .catch(() => setError('Failed to reach server'));
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const summary = data?.summary;

  return (
    <div className="list-page">
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
      `}</style>

      {/* Header */}
      <div className="page-hd" style={{ marginBottom: 16 }}>
        <div className="page-hd-left">
          <h1 className="page-title">Live Dialer Monitor</h1>
          <p className="page-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: error ? '#ef4444' : '#22c55e', display: 'inline-block' }} />
            <span style={{ fontWeight: 600, color: error ? '#ef4444' : '#22c55e' }}>
              {error ? 'Connection error' : 'Live Dialer Activity'}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              · Auto-refresh every 5s · Last update: {lastUpdate ? fmtTimeAgo(lastUpdate.toISOString()) : '—'}
            </span>
          </p>
        </div>
        <div className="page-hd-right">
          <button className="btn btn-ghost" onClick={load}>↺ Refresh</button>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
        <StatCard
          label="On Call Now"
          value={summary?.on_call_count ?? '—'}
          color="#22c55e"
          live
        />
        <StatCard
          label="Idle (synced)"
          value={summary?.idle_count ?? '—'}
          color="var(--navy)"
        />
        <StatCard
          label="Offline >1hr"
          value={summary?.offline_count ?? '—'}
          color={summary?.offline_count > 0 ? '#ef4444' : 'var(--navy)'}
        />
        <StatCard
          label="Total Today"
          value={summary?.total_today ?? '—'}
          color="var(--navy)"
        />
        <StatCard
          label="Live Talk"
          color="#22c55e"
          live
          value={
            summary !== undefined
              ? <TalkTimer initialSecs={summary.live_talk_seconds} />
              : '—'
          }
        />
      </div>

      {/* Active calls */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>
            Active Calls ({summary?.on_call_count ?? 0})
          </span>
        </div>

        {data?.on_call?.length > 0 ? (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {data.on_call.map((agent: any) => (
              <ActiveCallCard key={agent.device_id} agent={agent} />
            ))}
          </div>
        ) : (
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '24px', color: 'var(--text-muted)', fontSize: 14, textAlign: 'center' }}>
            No active calls right now
          </div>
        )}
      </div>

      {/* Idle agents */}
      {data?.idle?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              Idle Agents ({data.idle.length})
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13 }}> — Sync active</span>
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {data.idle.map((agent: any) => (
              <AgentChip key={agent.device_id} agent={agent} />
            ))}
          </div>
        </div>
      )}

      {/* Offline agents */}
      {data?.offline?.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#94a3b8', border: '2px solid #cbd5e1', display: 'inline-block' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              Offline ({data.offline.length})
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {data.offline.map((agent: any) => (
              <div key={agent.device_id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 10, padding: '8px 14px', opacity: 0.55,
              }}>
                <Avatar name={agent.agent_name} size={28} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{agent.agent_name}</div>
                  <div style={{ fontSize: 11, color: '#ef4444' }}>
                    sync {fmtTimeAgo(agent.last_seen_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!data && !error && (
        <div className="spinner">Loading…</div>
      )}
    </div>
  );
};

export default LiveMonitorPage;
